import React from "react";

import { faXmark } from "@fortawesome/pro-solid-svg-icons";

import { faCircleCheck, faCirclePlus } from "@performyard/fontawesome/pro-solid-svg-icons";
import { ButtonIcon, ButtonStandard, Text } from "@performyard/py-components";

import { GoalEditCard } from "./GoalEditCard";

import type { EditableGoalFields, GoalSuggestion } from "../types";

import { useAskPerformYardStore } from "../AskPerformYardStore";

type GoalEditPanelProps = {
	onClose: () => void;
	onCreateGoal: (suggestion: GoalSuggestion, editedValues?: Partial<EditableGoalFields>) => Promise<void>;
	onCreateAllGoals: () => Promise<void>;
	onDeleteGoal: (suggestionId: string) => void;
	creatingSuggestionIds: Set<string>;
	isCreatingAll: boolean;
};

export const GoalEditPanel: React.FC<GoalEditPanelProps> = ({
	onClose,
	onCreateGoal,
	onCreateAllGoals,
	onDeleteGoal,
	creatingSuggestionIds,
	isCreatingAll,
}) => {
	const goalSuggestionStates = useAskPerformYardStore((state) => state.goalSuggestionStates);
	const visibleSuggestions = goalSuggestionStates.filter((gs) => gs.status !== "deleted");
	const pendingSuggestions = goalSuggestionStates.filter((gs) => gs.status === "pending");
	const createdSuggestions = goalSuggestionStates.filter((gs) => gs.status === "created");
	const allResolved = pendingSuggestions.length === 0;

	const emptyMessage =
		createdSuggestions.length > 0 ? "All goals have been created!" : "All goal suggestions have been removed.";

	return (
		<div className="flex h-full min-w-0 flex-col overflow-hidden">
			{/* Header Row — matches ChatHeader height and border */}
			<div className="box-border flex w-full shrink-0 items-center gap-2 bg-white p-2 pr-6">
				<div className="flex min-w-0 flex-1 items-center">
					<div className="flex items-center justify-center p-2">
						<ButtonIcon
							icon={faXmark}
							onClick={onClose}
							variant="standard"
							color="neutral"
							shade={5}
							size={20}
							aria-label="Close edit panel"
						/>
					</div>
				</div>
				{allResolved ? (
					<ButtonStandard
						variant="text"
						color="green"
						size="medium"
						iconLeft={faCircleCheck}
						dangerouslySetClassName="!w-auto !cursor-default"
					>
						Goals Created
					</ButtonStandard>
				) : (
					<ButtonStandard
						variant="text"
						color="primary"
						size="medium"
						iconLeft={faCirclePlus}
						onClick={onCreateAllGoals}
						disabled={isCreatingAll}
						dangerouslySetClassName="!w-auto"
					>
						{isCreatingAll ? "Creating..." : "Create All Goals"}
					</ButtonStandard>
				)}
			</div>

			{/* Content — scrollable */}
			<div className="min-w-0 flex-1 overflow-y-auto bg-white px-4 pb-6 pt-4">
				{visibleSuggestions.length === 0 ? (
					<div className="flex items-center justify-center py-8">
						<Text size="paragraph-3" color="neutral-6">
							{emptyMessage}
						</Text>
					</div>
				) : (
					/* Grouped Goals container — bg-neutral-1 rounded-lg */
					<div className="mx-auto flex w-[480px] min-w-0 flex-col gap-4 rounded-lg bg-neutral-1 px-4 py-2">
						{/* Section title */}
						<div className="flex h-6 items-center">
							<Text size="paragraph-3" weight="semi-bold" color="neutral-9">
								Goals
							</Text>
						</div>

						{/* Goal cards */}
						{visibleSuggestions.map((gs) => (
							<GoalEditCard
								key={gs.suggestion.id}
								suggestionState={gs}
								onCreateGoal={(editedValues) => onCreateGoal(gs.suggestion, editedValues)}
								onDeleteGoal={() => onDeleteGoal(gs.suggestion.id)}
								isCreating={creatingSuggestionIds.has(gs.suggestion.id)}
							/>
						))}
					</div>
				)}
			</div>
		</div>
	);
};

export default GoalEditPanel;
